import "./sitekit.css";
import { incompatibility, readProviders, readSatellites, readPublications, publicationAccepted, type ProviderView, type PublicationView, type SatelliteView } from "./publicationModel";
import { api, ApiError } from "../../api";
import { mountViewer, openViewerOverlay, type ViewerHandle, type ViewerOptions, type ViewerState } from "../sitepreview/viewer";
import { errorBanner, loadingBlock, pill, tableRegion } from "../shared/blocks";
import { intentFor, resolveIntent } from "../shared/intent";
import { createLifecycle, isAbort } from "../shared/lifecycle";
import { h, tidy } from "../shared/safe";
import { mountSuppliedForm, type SuppliedFormHandle } from "./supplied/form";
import { isSuppliedKey, type Choices } from "./supplied/model";
import { busyButton, card, labelled, notice, select, textArea, textInput, webLink } from "../facts/kit";
import { describeMediaProblem, sha8, type MediaProblem } from "../media/mediaModel";
import { clientWorkspaceNav } from "../client/workspaceNav";
import { parseMediaList, parseRevisions, type MediaRevision } from "../media/mediaModel";
import {
  HOSTS_NOTE,
  composeBody,
  composeProblems,
  emptyCompose,
  parseBuildList,
  parseBuildReply,
  parseComposeReply,
  parseKits,
  parsePreview,
  parseReleaseList,
  parseReleaseReply,
  parseRevisionList,
  parseSiteDetail,
  parseSiteReply,
  parseSites,
  VISIT_PHOTO_ROLE,
  remainingText,
  type BuildView,
  type ComposeDraft,
  type Composition,
  type KitView,
  type PreviewView,
  type ReleaseView,
  type RevisionView,
  type SiteDetail,
  type SiteView,
} from "./siteKitModel";

// Site kit: #/clients/:id/site. Pick a kit (browse them in Template Studio, #/templates), compose a site revision from this client's APPROVED records (the composer names
// exactly what is missing and never invents it), build it (a draft), look at every page through a signed preview link, and record a build as
// the current release. A release is a LOCAL record: it publishes nothing to any host; that is a separate step.

const enc = encodeURIComponent;

interface SiteState {
  publications: PublicationView[] | null;
  publicationError: string | null;
  publishNote: string | null;
  confirmingPublish: boolean;
  publishPlatform: string;
  detail: SiteDetail | null;
  revisions: RevisionView[] | null;
  builds: BuildView[] | null;
  releases: ReleaseView[] | null;
  draft: ComposeDraft;
  composeResult: { composition: Composition; revision: RevisionView } | null;
  composeProblem: MediaProblem | null;
  build: BuildView | null;
  buildProblem: MediaProblem | null;
  preview: PreviewView | null;
  viewerState: Partial<ViewerState>;
  previewProblem: MediaProblem | null;
  releaseNote: string | null;
  releaseProblem: MediaProblem | null;
  confirmingRelease: boolean;
  loadError: string | null;
}
const blank = (): SiteState => ({ publications: null, publicationError: null, publishNote: null, confirmingPublish: false, publishPlatform: "", detail: null, revisions: null, builds: null, releases: null, draft: emptyCompose(), composeResult: null, composeProblem: null, build: null, buildProblem: null, preview: null, viewerState: {}, previewProblem: null, releaseNote: null, releaseProblem: null, confirmingRelease: false, loadError: null });

export async function clientSiteKitView(root: HTMLElement, params: Record<string, string>): Promise<void> {
  const shell = h("div", { class: "qf qf-cd fx sk" });
  root.replaceChildren(shell);
  const clientId = typeof params.id === "string" ? params.id : "";
  if (clientId === "") return void shell.append(errorBanner("This page needs a client. Go back to the Fleet and open one."));
  const hashQuery = window.location.hash.includes("?") ? window.location.hash.slice(window.location.hash.indexOf("?") + 1) : "";
  const routeQuery = new URLSearchParams(hashQuery);
  const selectedSiteId = routeQuery.get("siteId");
  const requestedPlatform = routeQuery.get("platform") ?? "";
  const life = createLifecycle(shell);
  const base = `/v1/clients/${enc(clientId)}`;

  shell.append(
    h("div", { class: "topbar" }, h("h1", null, "Site kit"), h("div", { class: "btn-group" }, h("a", { class: "btn secondary", href: `#/clients/${enc(clientId)}` }, "← Client"), h("a", { class: "btn secondary", href: `#/clients/${enc(clientId)}/facts` }, "Facts & targets"), h("a", { class: "btn secondary", href: `#/clients/${enc(clientId)}/content` }, "Content"), h("a", { class: "btn secondary", href: `#/clients/${enc(clientId)}/media` }, "Media"))),
    clientWorkspaceNav(clientId, "Websites"),
    h("div", { class: "ct-banner", role: "note", id: "sk-banner" }, h("strong", null, "Build, review and publish a complete site. "), "A kit builds a complete website from this client's approved records. A build is a draft. A release only records, in this computer's database, that a build is the site's current release: publishing that exact build to a connected provider is a separate action below."),
  );

  // ---- state -----------------------------------------------------------------------------------------------------------------------
  let providers: ProviderView[] = [];
  let providerError: string | null = null;
  let selectedProvider = requestedPlatform;
  let kits: KitView[] | null = null;
  let composesFrom: Record<string, string> = {};
  let kitsError: string | null = null;
  let sites: SiteView[] | null = null;
  let sitesError: string | null = null;
  let selectedLegacy: Record<string, unknown> | null = null;
  let approvedMedia: MediaRevision[] = [];
  let services: string[] = [];
  let approvedContent: Choices["content"] = [];
  const state = new Map<string, SiteState>();
  const st = (id: string): SiteState => {
    let s = state.get(id);
    if (!s) state.set(id, (s = blank()));
    return s;
  };
  const timers = new Set<ReturnType<typeof setInterval>>();
  window.addEventListener("hashchange", () => {
    if (!shell.isConnected) timers.forEach((t) => clearInterval(t));
  });

  const siteHost = h("div", { id: "sk-sites", class: "sk-sites" });
  const flash = h("div", { id: "sk-flash", "aria-live": "polite" });

  // ---- loading ---------------------------------------------------------------------------------------------------------------------
  async function loadKits() {
    try {
      const r = parseKits(await api<unknown>("/v1/site-kits"));
      if (!life.alive()) return;
      kits = r?.kits ?? null;
      if (kits && chosenKit && !kits.some((k) => k.kitId === chosenKit && k.composable)) chosenKit = ""; // an unknown or not-composable template is never preselected
      composesFrom = r?.composesFrom ?? {};
      kitsError = r ? null : "The server sent kits this page does not understand.";
    } catch (e) {
      if (!life.alive() || isAbort(e)) return;
      kitsError = e instanceof ApiError ? `The kits could not be loaded (HTTP ${e.status}).` : "Couldn't reach the server.";
    }
  }
  async function loadProviders() {
    try { const response = await api<unknown>(`${base}/kit-publication-options`); const parsed = readProviders(response); satellites = readSatellites(response); providers = parsed ?? []; providerError = parsed ? null : "Provider capabilities could not be read."; }
    catch { providerError = "Provider capabilities could not be loaded. Check connections and refresh."; }
    if (!selectedProvider) selectedProvider = providers.find(p => p.supported && p.connected)?.platform ?? "";
    paintProviders();
  }
  async function loadAux() {
    try {
      const cursors = new Set<string>();
      let after: string | null = null;
      const found: MediaRevision[] = [];
      do {
        const query = new URLSearchParams({ limit: "50" });
        if (after) query.set("after", after);
        const page = parseMediaList(await api<unknown>(`${base}/media?${query.toString()}`));
        if (!page) throw new Error("Unrecognized media list response");
        // Keep revision reads bounded to one server page at a time; clients can have more than 50 images.
        const revisions = await Promise.all(page.items.map(async (it) => parseRevisions(await api<unknown>(`${base}/media/${enc(it.mediaId)}/revisions`)) ?? []));
        for (const list of revisions) for (const r of list) if (r.approval.state === "approved") found.push(r);
        if (page.next && (page.next === after || cursors.has(page.next))) throw new Error("Media pagination cursor repeated");
        after = page.next;
        if (after) cursors.add(after);
      } while (after);
      if (life.alive()) approvedMedia = found;
    } catch {
      /* media may be unavailable (no object storage): the compose form then has no images to pin */
    }
    try {
      const f = await api<{ facts?: { approved?: { services_json?: unknown } } }>(`${base}/facts`);
      const sv = f.facts?.approved?.services_json;
      if (life.alive() && Array.isArray(sv)) services = sv.map((s) => (s && typeof s === "object" ? (s as { name?: unknown }).name : null)).filter((n): n is string => typeof n === "string");
    } catch {
      /* optional */
    }
    try {
      // Approved and published content is what a site can use. The guided entries offer exactly these as choices.
      const found = new Map<string, Choices["content"][number]>();
      for (const status of ["approved", "published"]) {
        const r = await api<{ content?: { id?: unknown; title?: unknown; topic?: { title?: unknown } | null }[] }>(`${base}/content?status=${status}&limit=200`);
        for (const c of Array.isArray(r.content) ? r.content : []) if (typeof c.id === "string" && typeof c.title === "string") found.set(c.id, { id: c.id, title: tidy(c.title, 90), ...(typeof c.topic?.title === "string" && c.topic.title ? { topic: tidy(c.topic.title, 80) } : {}) });
      }
      if (life.alive()) approvedContent = [...found.values()];
    } catch {
      /* optional: the guided entries then say there is no approved content to choose from */
    }
  }
  async function loadSites() {
    try {
      const r = parseSites(await api<unknown>(`${base}/kit-sites`));
      if (!life.alive()) return;
      sites = r;
      sitesError = r ? null : "The server sent sites this page does not understand.";
      if (r && selectedSiteId && !selectedSiteId.startsWith("legacy:")) {
        if (r.some((s) => s.siteId === selectedSiteId)) await loadSite(selectedSiteId);
      } else if (selectedSiteId?.startsWith("legacy:")) {
        try {
          const response = await api<unknown>(`${base}/websites`);
          const list = response && typeof response === "object" && Array.isArray((response as { websites?: unknown }).websites) ? (response as { websites: Record<string, unknown>[] }).websites : [];
          selectedLegacy = list.find((site) => site.websiteId === selectedSiteId) ?? null;
        } catch { selectedLegacy = null; }
      }
    } catch (e) {
      if (!life.alive() || isAbort(e)) return;
      sitesError = e instanceof ApiError ? (e.status === 404 ? "This client was not found." : `The sites could not be loaded (HTTP ${e.status}).`) : "Couldn't reach the server.";
    }
  }
  async function loadSite(siteId: string) {
    const s = st(siteId);
    try {
      const [detail, revs, builds, rels] = await Promise.all([
        api<unknown>(`${base}/kit-sites/${enc(siteId)}`),
        api<unknown>(`${base}/kit-sites/${enc(siteId)}/revisions`),
        api<unknown>(`${base}/kit-sites/${enc(siteId)}/builds`),
        api<unknown>(`${base}/kit-sites/${enc(siteId)}/releases`),
      ]);
      if (!life.alive()) return;
      s.detail = parseSiteDetail(detail);
      s.revisions = parseRevisionList(revs);
      s.builds = parseBuildList(builds);
      s.releases = parseReleaseList(rels);
      await loadPublications(siteId);
      s.loadError = s.detail ? null : "The server sent a site this page does not understand.";
      if (s.build && s.builds) s.build = s.builds.find(b => b.recordId === s.build!.recordId) ?? s.build;
      if (!s.build && s.builds && s.builds.length) s.build = s.builds.reduce((a, b) => ((b.createdAt?.iso ?? "") > (a.createdAt?.iso ?? "") ? b : a)); // the newest build, whatever the order
    } catch (e) {
      if (!life.alive() || isAbort(e)) return;
      s.loadError = e instanceof ApiError ? `This site could not be loaded (HTTP ${e.status}).` : "Couldn't reach the server.";
    }
  }

  async function loadPublications(siteId: string) {
    const s = st(siteId);
    try { s.publications = readPublications(await api<unknown>(`${base}/kit-sites/${enc(siteId)}/publications`)); s.publicationError = s.publications ? null : "Publication history could not be read."; }
    catch { s.publicationError = "Publication history could not be loaded. Refresh to check the actual outcome before publishing again."; }
  }
  // ---- create a site ---------------------------------------------------------------------------------------------------------------
  let satellites: SatelliteView[] = [];
  let selectedSatellite = "";
  let siteKind: "main" | "satellite" | "independent" = requestedPlatform ? "independent" : "main";
  // a template chosen in Template Studio (#/clients/:id/site/:kitId) is preselected; it is checked against the server's kit list once that arrives
  let chosenKit = typeof params.kitId === "string" ? params.kitId : "";
  let rootUrl = "";
  let urlStyle = "clean";
  let siteIdInput = "";
  let providerSlug = "";
  const createResult = h("div", { id: "sk-create-result", "aria-live": "polite" });
  const kitList = h("div", { class: "sk-kits", role: "radiogroup", "aria-label": "Site kit" });
  const providerHost = h("div");
  const createForm = h("form", { class: "fx-form", novalidate: true, id: "sk-create-form", "aria-label": "Create a site" });

  function paintProviders() {
    const kind = select([["main", "Client main site"], ["satellite", "Existing satellite"], ["independent", "Separate website on this provider"]], siteKind, { name: "siteKind", onChange: v => {
      siteKind = v as typeof siteKind; selectedSatellite = ""; rootUrl = ""; rootInput.value = ""; paintProviders();
      createResult.replaceChildren(notice("warn", "Website destination context changed. Choose its provider slug and check the provider destination."));
    } });
    const scope = select([["", "Client main site"], ...satellites.map(s => [s.id, `${s.name} (${s.slug})`] as [string,string])], selectedSatellite, { name: "satelliteId", onChange: v => {
      selectedSatellite = v; rootUrl = ""; rootInput.value = ""; createResult.replaceChildren(notice("warn", "Destination context changed. Select the provider destination for this site before creating it."));
    } });
    const slug = textInput(providerSlug, { name: "providerSlug", placeholder: "unique provider site slug", onInput: v => { providerSlug = v.trim().toLowerCase(); } });
    const choices = select([["", "Choose a connected provider"], ...providers.filter(p => p.supported && p.connected).map(p => [p.platform, p.platform] as [string,string])], selectedProvider, { name: "destinationProvider", onChange: v => { selectedProvider = v; } });
    const use = busyButton({ label: "Use provider destination", busyLabel: "Checking destination…", id: "sk-use-provider-destination", onClick: async () => {
      if (!selectedProvider) return;
      if (siteKind === "independent" && !providerSlug) return void createResult.replaceChildren(notice("bad", "Enter a unique provider site slug first."));
      const providerAtRequest = selectedProvider, satelliteAtRequest = selectedSatellite, kindAtRequest = siteKind, slugAtRequest = providerSlug;
      try {
        const r = await api<{ destination: { root: string; urlStyle: string } }>(`${base}/kit-publication-destination`, { method: "POST", body: JSON.stringify({ platform: providerAtRequest, ...(kindAtRequest === "satellite" && satelliteAtRequest ? { satelliteId: satelliteAtRequest } : {}), ...(kindAtRequest === "independent" ? { providerSlug: slugAtRequest } : {}) }) });
        if (!life.alive() || providerAtRequest !== selectedProvider || satelliteAtRequest !== selectedSatellite || kindAtRequest !== siteKind || (kindAtRequest === "independent" && slugAtRequest !== providerSlug)) return;
        rootUrl = r.destination.root; urlStyle = r.destination.urlStyle; rootInput.value = rootUrl; styleSel.value = urlStyle;
        createResult.replaceChildren(notice("ok", "Provider destination selected. Choose a template and create the site before building."));
      } catch (e) { showProblem(describeMediaProblem(e, "Destination unavailable"), createResult); }
    } });
    providerHost.replaceChildren(labelled("Website type", kind), ...(siteKind === "satellite" ? [labelled("Satellite", scope)] : []), ...(siteKind === "independent" ? [labelled("Provider site slug", slug, { help: "Choose a distinct resource name. CloudPush checks that the connected provider resolves it to the exact destination before sending." })] : []), labelled("Publication provider", choices), use,
      ...(providerError ? [notice("warn", providerError)] : []),
      h("details", null, h("summary", null, "Provider support and replacement behavior"), ...providers.map(p => h("p", null, `${p.platform}: ${p.supported ? (p.connected ? "connected" : "connect an account first") : "unsupported for template publication"}. ${p.replacement === "whole_site" ? "Replaces the whole site, including omitted files." : "Uploads included files; omitted files remain."} ${p.reason}`))));
  }

  function paintKits() {
    kitList.replaceChildren(
      ...(kitsError
        ? [errorBanner(kitsError, { retry: () => void loadKits().then(paintKits) })]
        : kits === null
          ? [loadingBlock("Loading kits", 2)]
          : kits.map((k) =>
              h(
                "label",
                { class: `sk-kit${chosenKit === k.kitId ? " sk-kit--on" : ""}`, dataset: { kit: k.kitId } },
                h("input", { type: "radio", name: "sk-kit", value: k.kitId, checked: chosenKit === k.kitId, on: { change: () => { chosenKit = k.kitId; paintKits(); } } }),
                h("strong", null, ` ${k.title}`),
                h("span", { class: "muted" }, ` · ${k.family} · v${k.version}${k.composable ? "" : " · not composable"}`),
                k.catalogue?.summary ? h("div", { class: "fx-small" }, k.catalogue.summary) : null,
                h("div", { class: "fx-small" }, `Pages: ${k.pages.map((p) => p.title).join(", ")}`),
                h("div", { class: "muted fx-small" }, `Needs: ${k.needs.join(", ")}`),
                k.catalogue?.supplied ? h("div", { class: "muted fx-small" }, `${k.catalogue.supplied.required ? "You also supply" : "Optionally you supply"}: ${k.catalogue.supplied.description}`) : null,
                h("a", { class: "fx-small", href: `#/templates/preview/${enc(k.kitId)}`, dataset: { kitPreview: k.kitId } }, "Preview complete site (sample data)"),
              ),
            )),
    );
  }
  const rootInput = textInput("", { name: "root", placeholder: "https://sites.example/my-client", onInput: (v) => void (rootUrl = v) });
  const idInput = textInput("", { name: "siteId", onInput: (v) => void (siteIdInput = v) });
  const styleSel = select([["clean", "Clean URLs (/services/)"], ["explicit_index", "Explicit index files (/services/index.html)"]], "clean", { name: "urlStyle", onChange: (v) => void (urlStyle = v) });
  const createBtn = busyButton({
    label: "Create site",
    busyLabel: "Creating…",
    id: "sk-create",
    type: "submit",
    onClick: async () => {
      createResult.replaceChildren();
      if (!chosenKit) return void createResult.replaceChildren(notice("bad", "Choose a kit first. Nothing was created."));
      if (!/^https?:\/\/\S+$/i.test(rootUrl.trim())) return void createResult.replaceChildren(notice("bad", "Enter the address the site will be hosted at later, starting with http:// or https://. Nothing is published there."));
      try {
        if (siteKind === "satellite" && !selectedSatellite) return void createResult.replaceChildren(notice("bad", "Choose an existing satellite for this website."));
        if (siteKind === "independent" && (!selectedProvider || !providerSlug)) return void createResult.replaceChildren(notice("bad", "Choose the connected platform and a provider site slug for this separate website."));
        const r = parseSiteReply(await api<unknown>(`${base}/kit-sites`, { method: "POST", body: JSON.stringify({ kitId: chosenKit, siteKind, ...(siteKind === "satellite" ? { satelliteId: selectedSatellite } : {}), ...(siteKind === "independent" ? { platform: selectedProvider, providerSlug } : {}), destination: { root: rootUrl.trim(), urlStyle }, ...(siteIdInput.trim() ? { siteId: siteIdInput.trim() } : {}) }) }));
        if (!life.alive()) return;
        createResult.replaceChildren(notice("ok", r ? `${r.replayed ? "This site already existed. " : "Site created. "}Next: compose a revision from the approved records. Nothing was published.` : "The server replied, but not with a site this page recognises. Reload to see what exists."));
        await loadSites();
        paintSites();
      } catch (e) {
        if (!life.alive() || isAbort(e)) return;
        showProblem(describeMediaProblem(e, "Site not created"), createResult);
      }
    },
  });
  createForm.onsubmit = (e) => {
    e.preventDefault();
    void createBtn.click();
  };
  createForm.append(
    kitList,
    providerHost,
    h("div", { class: "fx-grid" }, labelled("Where the site will be hosted (its root address)", rootInput, { help: "Only recorded. Nothing is published to this address.", wide: true }), labelled("URL style", styleSel), labelled("Site id (optional)", idInput)),
    h("div", { class: "btn-group" }, createBtn),
    createResult,
  );

  function showProblem(p: MediaProblem, into: HTMLElement) {
    into.replaceChildren(notice(p.kind === "network" || p.kind === "server" ? "warn" : "bad", p.message, p.fields.map((f) => (f.field ? `${f.field}: ${f.message}` : f.message))));
  }

  // ---- one site --------------------------------------------------------------------------------------------------------------------
  function siteCard(siteId: string): HTMLElement {
    const s = st(siteId);
    const d = s.detail;
    const kit = kits?.find((k) => k.kitId === d?.site.kitId);
    const body = h("div", { class: "sk-site__body" });
    const selected = selectedSiteId === siteId;
    const out = h("section", { class: "card qf-cd-card sk-site", id: `sk-site-${siteId}`, dataset: { siteId, selected: selected ? "true" : "false" }, "aria-labelledby": `sk-site-${siteId}-h` }, h("h2", { id: `sk-site-${siteId}-h` }, `Site ${siteId}${kit ? `: ${kit.title}` : ""}${selected ? " · selected website" : ""}`), body);
    if (s.loadError || !d) {
      body.append(s.loadError ? errorBanner(s.loadError, { retry: () => void loadSite(siteId).then(paintSites) }) : loadingBlock("Loading site", 2));
      return out;
    }
    const lr = d.latestRevision;
    body.append(
      h("p", { class: "muted", dataset: { siteContext: "true" } }, d.site.siteKind === "independent" ? `Separate website · ${d.site.platform ?? "provider not assigned"}` : d.site.satelliteId ? `Satellite: ${satellites.find(s => s.id === d.site.satelliteId)?.name ?? d.site.satelliteId}` : "Client main site"),
      ...(d.site.siteKind === "independent" ? [h("p", { class: "muted" }, `Separate ${d.site.platform ?? "provider"} website · resource ${d.site.providerSlug ?? "unknown"}`)] : []),
      ...(d.site.removedAt ? [notice("warn", "This website was intentionally removed. Its history is retained and publication is locked.")] : []),
      h(
        "div",
        { class: "fx-unr-head" },
        pill(`Revision ${d.site.latestRevision}`, "neutral"),
        pill(d.site.currentReleaseNumber ? `Release ${d.site.currentReleaseNumber} recorded (local)` : "No release recorded", d.site.currentReleaseNumber ? "ok" : "neutral"),
        h("span", { class: "muted" }, `will be hosted at ${d.site.root} (${d.site.urlStyle}); see publication history for delivery evidence`),
      ),
      ...(lr?.behindFacts ? [h("div", { class: "sk-behind", role: "alert", dataset: { behindFacts: "true" } }, h("strong", null, "Behind the facts: "), `revision ${lr.revision} was composed from ${lr.factsRevisionId}; the client's approved facts are now ${lr.factsRevisionNow}. Compose a new revision before building or releasing.`)] : []),
      composeStep(siteId, s),
      buildStep(siteId, s),
      previewStep(siteId, s),
      releaseStep(siteId, s),
      publicationStep(siteId, s),
    );
    return out;
  }

  function composeStep(siteId: string, s: SiteState): HTMLElement {
    const d = s.draft;
    const result = h("div", { "aria-live": "polite", dataset: { composeResult: "true" } });
    const missing = s.composeProblem?.fields ?? [];
    const copyMissing = (k: string) => missing.some((m) => m.field === k);
    const approveFacts = h("input", { type: "checkbox", id: `sk-af-${siteId}`, checked: d.approveFacts, on: { change: (e: Event) => void (d.approveFacts = (e.target as HTMLInputElement).checked) } });
    const approveContent = h("input", { type: "checkbox", id: `sk-ac-${siteId}`, checked: d.approveContent, on: { change: (e: Event) => void (d.approveContent = (e.target as HTMLInputElement).checked) } });
    const sitewideFooterLink = h("input", { type: "checkbox", id: `sk-footer-link-${siteId}`, checked: d.sitewideFooterLink, on: { change: (e: Event) => void (d.sitewideFooterLink = (e.target as HTMLInputElement).checked) } });
    const pins = h("div", { class: "sk-pins" });
    const drawPins = () => {
      pins.replaceChildren(
        ...(approvedMedia.length === 0 ? [h("p", { class: "muted" }, "No approved images. Approve an image revision on the Media page to use it here; unapproved images are refused.")] : []),
        ...approvedMedia.map((m) => {
          const cur = d.pins.find((p) => p.revisionId === m.revisionId);
          const role = select([["", "Not used"], ["hero", "Hero image"], ["logo", "Logo (site header)"], ...(s.detail?.site.kitId === "profile-ppf-signature" ? [[VISIT_PHOTO_ROLE, "Visit page photo (storefront)"] as [string, string]] : []), ...services.map((n): [string, string] => [n, `Image for service: ${n}`])], cur?.role ?? "", { name: `pin-${m.revisionId}`, onChange: (v) => {
            d.pins = d.pins.filter((p) => p.revisionId !== m.revisionId);
            if (v) d.pins.push({ mediaId: m.mediaId, revisionId: m.revisionId, role: v });
            supplied?.form.updateMediaChoices?.(pinnedMediaChoices(s));
          } });
          return h("div", { class: "sk-pin" }, labelled(`${tidy(m.filename, 60)} (revision ${m.revision}, sha256 ${sha8(m.sha256)}, approved)`, role));
        }),
      );
    };
    drawPins();
    const modeSel = select([["demo", "Demo (synthetic data; allowed on .test and .example)"], ["live", "Live (refuses reserved hosts such as .test and .example)"]], d.mode, { name: "mode", onChange: (v) => void (d.mode = v === "live" ? "live" : "demo") });
    const supplied = suppliedBlock(siteId, s);
    const compose = busyButton({
      label: "Compose revision from the approved records",
      busyLabel: "Composing…",
      id: `sk-compose-${siteId}`,
      onClick: async () => {
        const probs = composeProblems(d);
        const supErrors = supplied ? Object.values(supplied.form.validate()) : [];
        if (probs.length || supErrors.length) {
          result.replaceChildren(notice("bad", "Fix these first. Nothing was sent and everything you entered is still here.", [...probs.map((p) => p.message), ...(supErrors.length ? [`${supErrors.length} problem${supErrors.length === 1 ? "" : "s"} in the entries below, marked beside each field.`] : [])]));
          if (!probs.length) supplied?.form.focusFirstProblem();
          return;
        }
        try {
          const r = parseComposeReply(await api<unknown>(`${base}/kit-sites/${enc(siteId)}/revisions`, { method: "POST", body: JSON.stringify(composeBody(d, s.detail?.site.latestRevision ?? 0, supplied?.key ?? null)) }));
          if (!life.alive()) return;
          s.composeProblem = null;
          s.composeResult = r;
          s.build = null;
          s.preview = null;
          await loadSite(siteId);
          paintSites();
        } catch (e) {
          if (!life.alive() || isAbort(e)) return;
          s.composeProblem = describeMediaProblem(e, "Revision not composed");
          s.composeResult = null;
          paintSites();
          document.getElementById(`sk-compose-problem-${siteId}`)?.focus();
        }
      },
    });
    return h(
      "div",
      { class: "sk-step", dataset: { step: "compose" } },
      h("h3", null, "1. Compose a revision"),
      h("p", { class: "muted" }, "Everything comes from this client's approved records. If something the kit needs is missing, the composer names it below and nothing is saved: it never invents business facts, content or images."),
      h(
        "div",
        { class: "fx-grid" },
        labelled("Mode", modeSel, { help: HOSTS_NOTE, wide: true }),
        h("div", { class: "fx-field fx-field--wide" }, h("label", { for: `sk-footer-link-${siteId}` }, sitewideFooterLink, " Place the approved homepage link in the footer on every page"), h("p", { class: "muted" }, "Uses this client's active homepage target. This changes link placement only; it does not add or change a target.")),
        h("div", { class: "fx-field fx-field--wide" }, h("label", { for: `sk-af-${siteId}` }, approveFacts, " I confirm the client's current approved facts are what this site should be built from")),
        h("div", { class: "fx-field fx-field--wide" }, h("label", { for: `sk-ac-${siteId}` }, approveContent, " I confirm the approved content (and any copy I write below) may be used on this site")),
      ),
      h("h4", null, "Images (only approved revisions)"),
      pins,
      h("h4", null, "Copy that has no source in the records (only if the composer asks for it)"),
      h(
        "div",
        { class: "fx-grid" },
        labelled("Tagline", textInput(d.tagline, { name: "tagline", onInput: (v) => void (d.tagline = v) }), { help: copyMissing("copy.tagline") ? "The composer asked for this." : "Left empty it is derived from the category and city when the facts allow." }),
        labelled("Summary", textInput(d.summary, { name: "summary", onInput: (v) => void (d.summary = v) }), { help: copyMissing("copy.summary") ? "The composer asked for this." : "Left empty it is the client's claim block when there is one." }),
        labelled("About story (paragraphs separated by a blank line)", textArea(d.aboutStory, { name: "aboutStory", rows: 3, onInput: (v) => void (d.aboutStory = v) }), { wide: true, help: copyMissing("copy.aboutStory") ? "The composer asked for this." : undefined }),
        labelled("Primary colour (optional)", textInput(d.primaryHex, { name: "primaryHex", placeholder: "#1f4d3a", onInput: (v) => void (d.primaryHex = v) })),
        labelled("Accent colour (optional)", textInput(d.accentHex, { name: "accentHex", placeholder: "#8f4519", onInput: (v) => void (d.accentHex = v) })),
        labelled("Country (optional; recorded as an assumption otherwise)", textInput(d.country, { name: "country", onInput: (v) => void (d.country = v) })),
      ),
      supplied?.el ?? null,
      h("div", { class: "btn-group" }, compose),
      result,
      s.composeProblem
        ? h(
            "div",
            { class: "sk-missing", role: "alert", id: `sk-compose-problem-${siteId}`, tabindex: "-1", dataset: { composeProblem: "true" } },
            h("strong", null, "The revision was not saved."),
            h("p", null, s.composeProblem.message),
            s.composeProblem.fields.length ? h("ul", null, ...s.composeProblem.fields.map((f) => h("li", { dataset: { missing: f.field } }, h("strong", null, `${f.field}: `), f.message))) : null,
          )
        : null,
      s.composeResult ? compositionBlock(s.composeResult.revision, s.composeResult.composition) : null,
    );
  }

  function pinnedMediaChoices(s: SiteState): NonNullable<Choices["media"]> {
    return s.draft.pins.map(p => ({ id: p.mediaId, label: `${p.role === VISIT_PHOTO_ROLE ? "visit page photo" : p.role} image (${p.mediaId})` }));
  }

  /** Entries the owner supplies for kits whose collections are not stored records (directory listings, wiki references, area guides, magazine formats, authors), as a guided form. */
  function suppliedBlock(siteId: string, s: SiteState): { el: HTMLElement; form: SuppliedFormHandle; key: ReturnType<typeof keyOf> } | null {
    const sup = kits?.find((k) => k.kitId === s.detail?.site.kitId)?.catalogue?.supplied ?? null;
    const key = sup && isSuppliedKey(sup.key) ? sup.key : null;
    if (!sup || !key) return null;
    const form = mountSuppliedForm({ siteId, key, required: sup.required, description: sup.description, draft: s.draft.supplied, choices: { content: approvedContent, services, media: pinnedMediaChoices(s) }, serverProblems: (s.composeProblem?.fields ?? []).filter((f) => f.field.startsWith("supplied")) });
    return { el: form.el, form, key };
  }
  const keyOf = (k: string) => (isSuppliedKey(k) ? k : null);

  function compositionBlock(rev: RevisionView, c: Composition): HTMLElement {
    return h(
      "div",
      { class: "sk-composition", dataset: { composition: "true" } },
      h("p", null, h("strong", null, `Revision ${rev.revision} saved`), ` from ${rev.factsRevisionId || "the approved facts"} (approved by ${rev.createdBy ?? "unknown"}). It uses ${c.journalCount} approved content ${c.journalCount === 1 ? "entry" : "entries"} and ${c.mediaCount} approved ${c.mediaCount === 1 ? "image" : "images"}.`),
      c.derived.length ? h("details", { open: true }, h("summary", null, "Derived from the approved records"), h("ul", null, ...c.derived.map((x) => h("li", null, x)))) : null,
      c.assumptions.length ? h("details", { open: true, dataset: { assumptions: "true" } }, h("summary", null, "Assumptions (values not in the records, and the stated default)"), h("ul", null, ...c.assumptions.map((x) => h("li", null, x)))) : null,
      c.excluded.length ? h("details", { open: true }, h("summary", null, "Left out"), h("ul", null, ...c.excluded.map((x) => h("li", null, `${x.what}: ${x.why}`)))) : null,
    );
  }

  function buildStep(siteId: string, s: SiteState): HTMLElement {
    const revs = s.revisions ?? [];
    const latest = revs.reduce<RevisionView | null>((a, r) => (a === null || r.revision > a.revision ? r : a), null); // the server lists newest first; never rely on the order
    const result = h("div", { "aria-live": "polite" });
    const history = h("div", { class: "sk-version-history", "aria-label": "Saved version history" }, h("h4", null, "Saved versions"));
    if (!revs.length) history.append(h("p", { class: "muted" }, "No saved versions yet."));
    for (const rev of revs) history.append(h("div", { class: "sk-publication" }, h("p", null, `Revision ${rev.revision} · ${rev.createdAt} · approved by ${rev.createdBy ?? "unknown"}`), busyButton({ label: `Restore revision ${rev.revision} as a new draft`, busyLabel: "Restoring…", variant: "secondary", onClick: async () => {
      if (!window.confirm(`Create a new draft from saved revision ${rev.revision}? This does not change the current live website or erase later history.`)) return;
      try {
        await api(`${base}/kit-sites/${enc(siteId)}/revisions/${enc(rev.revisionId)}/restore`, { method: "POST", body: JSON.stringify({ expectedRevision: s.detail?.site.latestRevision ?? 0, confirmRestore: true, approvals: { facts: true, content: true } }) });
        await loadSite(siteId); paintSites();
      } catch (e) { result.replaceChildren(notice("warn", describeMediaProblem(e, "Version was not restored as a draft").message)); }
    } })));
    result.append(history);
    const build = busyButton({
      label: latest ? `Build revision ${latest.revision} (a draft)` : "Build (compose a revision first)",
      busyLabel: "Building…",
      id: `sk-build-${siteId}`,
      onClick: async () => {
        if (!latest) return void result.replaceChildren(notice("bad", "There is no revision to build yet. Compose one first."));
        try {
          const r = parseBuildReply(await api<unknown>(`${base}/kit-sites/${enc(siteId)}/builds`, { method: "POST", body: JSON.stringify({ revisionId: latest.revisionId }) }));
          if (!life.alive()) return;
          s.build = r?.build ?? null;
          s.buildProblem = r ? null : { kind: "other", message: "The server replied, but not with a build this page recognises.", fields: [], definite: false };
          s.preview = null;
          s.previewProblem = null;
          s.releaseNote = null;
          await loadSite(siteId);
          paintSites();
        } catch (e) {
          if (!life.alive() || isAbort(e)) return;
          s.buildProblem = describeMediaProblem(e, "Not built");
          paintSites();
        }
      },
    });
    return h(
      "div",
      { class: "sk-step", dataset: { step: "build" } },
      h("h3", null, "2. Build"),
      h("p", { class: "muted" }, "Builds the site files from the saved revision, checks them, and stores them locally. A build is a draft: it does not change the current release and publishes nothing."),
      h("div", { class: "btn-group" }, build),
      result,
      s.buildProblem ? h("div", { class: "sk-missing", role: "alert", dataset: { buildProblem: "true" } }, h("strong", null, "The build was refused."), h("p", null, s.buildProblem.message), s.buildProblem.fields.length ? h("ul", null, ...s.buildProblem.fields.map((f) => h("li", null, h("strong", null, `${f.field}: `), f.message))) : null) : null,
      s.build ? buildBlock(s.build, s.releases) : null,
    );
  }

  function buildBlock(b: BuildView, releases: ReleaseView[] | null): HTMLElement {
    const th = (t: string) => h("th", { scope: "col" }, t);
    // Builds stay immutable drafts; release records determine whether their artifacts were released.
    const releaseLabel = releases === null ? "Build stored locally" : releases.some(r => r.buildId === b.buildId) ? "Local release recorded" : "Draft: not released";
    return h(
      "div",
      { class: "sk-build", dataset: { build: b.recordId, buildState: b.state } },
      h("p", null, h("strong", null, "Build ready "), pill(b.state === "draft" ? releaseLabel : b.state, "neutral"), h("span", { class: "muted" }, ` ${b.pages.length} pages, ${b.fileCount} files, ${b.assets.length} images · release hash ${sha8(b.releaseHash)}${b.createdAt ? ` · ${b.createdAt.text}` : ""}`)),
      b.freshness && !b.freshness.fresh ? notice("warn", "This build is stale. Compose a new revision, build it and review its preview.", b.freshness.reasons) : null,
      h("h4", null, "Pages"),
      tableRegion("Pages of this build", h("table", { class: "qf-table", dataset: { table: "pages" } }, h("thead", null, h("tr", null, th("Page"), th("File"), th("Address once hosted"))), h("tbody", null, ...b.pages.map((p) => h("tr", { dataset: { page: p.pageId } }, h("td", { class: "qf-wrap" }, p.title), h("td", { class: "qf-wrap" }, p.path), h("td", { class: "qf-wrap" }, p.predictedUrl || "—")))))),
      b.assets.length ? h("p", { class: "muted" }, `Images used (exact approved revisions): ${b.assets.map((a) => `${a.publishedPath} (sha256 ${sha8(a.sha256)})`).join("; ")}`) : null,
      h("h4", null, `Intended links (${b.plannedLinks.length})`),
      h("p", { class: "muted" }, "These are PLANNED: what this build contains. Nothing says they were delivered to any host or observed anywhere; links CloudPush actually delivers appear in the Created links report."),
      tableRegion("Intended links of this build", h("table", { class: "qf-table", dataset: { table: "links" } }, h("thead", null, h("tr", null, th("On page"), th("Anchor"), th("Points to"), th("Kind"), th("rel"))), h("tbody", null, ...b.plannedLinks.map((l) => h("tr", { dataset: { plannedLink: l.href } }, h("td", { class: "qf-wrap" }, l.sourcePageId), h("td", { class: "qf-wrap" }, l.anchorText || "—"), h("td", { class: "qf-wrap" }, webLink(l.href)), h("td", null, `${l.scope} · ${l.linkClass}`), h("td", null, l.rel.length ? l.rel.join(" ") : "—")))))),
      b.diagnostics.length ? h("details", null, h("summary", null, `Notes from the build (${b.diagnostics.length})`), h("ul", null, ...b.diagnostics.map((d) => h("li", null, `${d.severity}: ${d.code}: ${d.message}`)))) : null,
    );
  }

  function previewStep(siteId: string, s: SiteState): HTMLElement {
    const b = s.build;
    const host = h("div", { class: "sk-preview" });
    const mint = busyButton({
      label: s.preview ? "Create a new preview link" : "Create a preview link",
      busyLabel: "Creating…",
      id: `sk-preview-${siteId}`,
      onClick: async () => {
        if (!b) return;
        try {
          const p = parsePreview(await api<unknown>(`${base}/kit-sites/${enc(siteId)}/builds/${enc(b.recordId)}/preview-link`, { method: "POST", body: JSON.stringify({ ttlSeconds: 900 }) }));
          if (!life.alive()) return;
          s.preview = p;
          s.previewProblem = p ? null : { kind: "other", message: "The server replied, but not with a preview link this page recognises.", fields: [], definite: false };
          paintSites();
        } catch (e) {
          if (!life.alive() || isAbort(e)) return;
          s.previewProblem = describeMediaProblem(e, "No preview link");
          paintSites();
        }
      },
    });
    if (s.preview && b) {
      const p = s.preview;
      const left = h("span", { class: "muted", dataset: { remaining: "true" }, "aria-live": "off" });
      const viewerHost = h("div", { class: "sk-viewer" });
      const dirOf = (path: string) => path.replace(/index\.html$/, "");
      const pages = b.pages.filter((x) => x.kind !== "redirect_stub").map((x) => ({ id: x.pageId, title: x.title, group: x.path.includes("/") ? x.path.split("/")[0]! : "Home", src: new URL(`site/${dirOf(x.path)}`, p.url).href }));
      const viewerOpts = (extra: ViewerOptions["extra"] = []): ViewerOptions => ({
        kind: "client", idPrefix: `sk-${siteId}`, name: `Build ${b.buildId.slice(0, 12)}`, siteSrc: new URL("site/", p.url).href, pages, liveLabel: "Live site",
        notice: `This is the client's ACTUAL build ${b.buildId.slice(0, 16)}, made from this client's approved records. Previewing this build sends nothing. Check its release and publication history below. It is not a template demo.`,
        state: s.viewerState, onState: (v) => void (s.viewerState = v), extra,
        expiredMessage: "The preview link expired (it lasts 15 minutes and reads only this build). Create a new link to look again.",
      });
      let viewer: ViewerHandle | null = null;
      let overlay: { close(): void; handle: ViewerHandle } | null = null;
      const full = h("button", { type: "button", class: "secondary", id: `sk-fullsize-${siteId}`, dataset: { preview: "fullsize" }, on: { click: () => {
        if (overlay) return;
        overlay = openViewerOverlay({ ...viewerOpts(), title: `Client build ${b.buildId.slice(0, 16)} (actual site, not a demo)`, onClose: () => { overlay = null; } });
      } } }, "Full-size viewer");
      const draw = () => {
        const r = remainingText(p.expiresAt);
        if (r === null) {
          left.textContent = "This preview link has expired.";
          viewer?.expire();
          overlay?.handle.expire();
          if (!viewer) viewerHost.replaceChildren(h("div", { class: "sk-expired", role: "status", dataset: { expired: "true" } }, "The preview link expired (it lasts 15 minutes and reads only this build). Create a new link to look again."));
          return false;
        }
        left.textContent = `Expires in ${r} (${p.expiresText})`;
        return true;
      };
      const live = draw();
      if (live) {
        viewer = mountViewer(viewerHost, viewerOpts());
        const timer = setInterval(() => {
          if (!life.alive()) { clearInterval(timer); overlay?.close(); return; }
          if (!draw()) {
            clearInterval(timer);
            timers.delete(timer);
          }
        }, 1000);
        timers.add(timer);
      }
      host.append(
        h("div", { class: "fx-unr-head" }, pill(`${p.pages} pages`, "neutral"), left, live ? full : null, h("a", { href: p.url, target: "_blank", rel: "noopener noreferrer", id: `sk-open-${siteId}`, class: "btn secondary", dataset: { preview: "open" } }, "Open in a new tab")),
        h("p", { class: "muted fx-small" }, "The link itself is the credential: anyone who has it can read this one preview until it expires. It reads nothing else. Do not share it."),
        viewerHost,
      );
    }
    return h(
      "div",
      { class: "sk-step", dataset: { step: "preview" } },
      h("h3", null, "3. Look at every page (preview)"),
      h("p", { class: "muted" }, b ? "A short-lived signed link opens this build's own pages and images so you can check them before anything else happens: the live site (click through it like a visitor), or every page at once, on a desktop or mobile screen. Nothing here is a template demo or sample data." : "Build first to get a preview."),
      ...(b ? [h("div", { class: "fx-unr-head", role: "note", "aria-label": "Build change summary" }, h("strong", null, "Review summary: "), `${b.pages.length} pages · ${b.fileCount} files · ${b.assets.length} approved images · ${b.plannedLinks.length} planned links · revision ${b.siteRevisionId} · ${b.baseReleaseNumber ? `based on release ${b.baseReleaseNumber}` : "first saved version"} · destination ${s.detail?.site.root ?? "unknown"} · build hash ${b.releaseHash}. Review every page and target below before recording or publishing.`)] : []),
      b ? h("div", { class: "btn-group" }, mint) : null,
      s.previewProblem ? h("div", { class: "sk-missing", role: "alert" }, s.previewProblem.message, ...s.previewProblem.fields.map((f) => h("div", null, f.message))) : null,
      host,
    );
  }

  function releaseStep(siteId: string, s: SiteState): HTMLElement {
    const b = s.build;
    const d = s.detail!;
    const result = h("div", { "aria-live": "polite" });
    const go = busyButton({
      label: "Confirm: record this build as the current release (local record)",
      busyLabel: "Recording…",
      id: `sk-release-confirm-${siteId}`,
      onClick: async () => {
        if (!b) return;
        const scope = `release|${siteId}|${b.recordId}|${d.site.currentReleaseNumber}`;
        const intent = intentFor(scope);
        try {
          const r = parseReleaseReply(await api<unknown>(`${base}/kit-sites/${enc(siteId)}/releases`, { method: "POST", body: JSON.stringify({ buildRecordId: b.recordId, expectedCurrentReleaseNumber: d.site.currentReleaseNumber, idempotencyKey: intent.key }) }));
          if (!life.alive()) return;
          resolveIntent(scope);
          s.confirmingRelease = false;
          s.releaseProblem = null;
          s.releaseNote = r ? `${r.replayed ? "Already recorded: " : ""}Release ${r.release.releaseNumber} is recorded in the local database as this site's current release. ${r.note ?? "Nothing was sent to a host."}` : "The server replied, but not with a release this page recognises. Reload to see what was recorded.";
          await loadSite(siteId);
          paintSites();
        } catch (e) {
          if (!life.alive() || isAbort(e)) return;
          const p = describeMediaProblem(e, "Release not recorded");
          if (p.definite) resolveIntent(scope);
          s.releaseProblem = p;
          paintSites();
        }
      },
    });
    return h(
      "div",
      { class: "sk-step", dataset: { step: "release" } },
      h("h3", null, "4. Release (local record only)"),
      h("p", { class: "muted" }, "Records, on this computer, that a build is the site's current release. It does NOT publish the site to a host, send anything over the network, or create a publication: publishing to a host is a separate step."),
      b
        ? s.confirmingRelease
          ? h("div", { class: "fx-promote", role: "group", "aria-label": "Record a release" }, h("strong", null, "Record this build as the current release?"), h("p", null, `Build ${tidy(b.recordId, 40)} (revision ${tidy(b.siteRevisionId, 40)}, ${b.pages.length} pages, hash ${sha8(b.releaseHash)}) becomes release ${d.site.currentReleaseNumber + 1} in this computer's database. Nothing is published anywhere.`), h("div", { class: "btn-group" }, go, h("button", { type: "button", class: "secondary", on: { click: () => { s.confirmingRelease = false; paintSites(); } } }, "Cancel")))
          : h("div", { class: "btn-group" }, h("button", { type: "button", id: `sk-release-${siteId}`, disabled: d.latestRevision?.behindFacts === true, "aria-describedby": d.latestRevision?.behindFacts ? `sk-site-${siteId}-h` : undefined, on: { click: () => { s.confirmingRelease = true; paintSites(); } } }, "Release this build…"))
        : h("p", { class: "muted" }, "Build first."),
      result,
      s.releaseProblem ? h("div", { class: "sk-missing", role: "alert" }, s.releaseProblem.message, ...s.releaseProblem.fields.map((f) => h("div", null, f.message))) : null,
      s.releaseNote ? h("div", { class: "qf-cd-notice qf-cd-notice--ok", role: "status", dataset: { released: "true" } }, s.releaseNote) : null,
      s.releases && s.releases.length ? h("p", { class: "muted" }, `Recorded releases: ${s.releases.map((r) => `#${r.releaseNumber} (revision ${r.siteRevision})`).join(", ")}`) : null,
    );
  }

  function publicationStep(siteId: string, s: SiteState): HTMLElement {
    const b = s.build, local = s.releases?.find(r => r.buildId === b?.buildId && r.releaseNumber === s.detail?.site.currentReleaseNumber);
    if (!s.publishPlatform) s.publishPlatform = selectedProvider;
    const fits = (p: ProviderView) => (b ? incompatibility(p, b, s.detail?.site.urlStyle ?? "") : null);
    const providerSelect = select([["", "Choose provider"], ...providers.filter(p => p.supported && p.connected).map(p => [p.platform, fits(p) ? `${p.platform} (this build does not fit)` : p.platform] as [string,string])], s.publishPlatform, { name: "publishProvider", onChange: v => { s.publishPlatform = v; s.confirmingPublish = false; paintSites(); } });
    const confirm = busyButton({ label: "Confirm publication to provider", busyLabel: "Recording publication…", id: `sk-publish-confirm-${siteId}`, onClick: async () => {
      if (!b || !local || !s.publishPlatform) return;
      const scope = `kit-publish|${siteId}|${b.recordId}|${local.releaseId}|${s.publishPlatform}`;
      try {
        const r = await api<unknown>(`${base}/kit-sites/${enc(siteId)}/publications`, { method: "POST", body: JSON.stringify({ buildRecordId: b.recordId, releaseId: local.releaseId, platform: s.publishPlatform, expectedReleaseHash: b.releaseHash, idempotencyKey: intentFor(scope).key, confirmPublish: true }) });
        if (!life.alive()) return;
        if (!publicationAccepted(r)) throw new Error("Publication acknowledgement was not recognized. Read history before repeating the request.");
        resolveIntent(scope); s.confirmingPublish = false; s.publishNote = `${r.replayed ? "Existing" : "Accepted"} publication job ${r.jobId}. Acceptance records the intention; refresh history for its actual outcome.`; s.publicationError = null;
        await loadPublications(siteId); paintSites();
      } catch(e) { if (!life.alive() || isAbort(e)) return; const message = describeMediaProblem(e, "Publication was not confirmed").message; await loadPublications(siteId); s.publicationError = message; paintSites(); }
    } });
    const chosen = providers.find((p) => p.platform === s.publishPlatform);
    const misfit = chosen ? fits(chosen) : null;
    const eligible = !!b && !!local && b.freshness?.fresh === true && !!s.publishPlatform && !misfit && !s.detail?.site.removedAt && (!s.detail?.site.platform || s.detail.site.platform === s.publishPlatform);
    return h("div", { class: "sk-step", dataset: { step: "publication" } }, h("h3", null, "5. Publish to provider"),
      h("p", null, "Publishes the exact files and images in the approved build preview. A local release alone sends nothing. Provider acceptance and inspected live links are shown separately."),
      s.detail?.site.removedAt ? notice("warn", "This saved website is locked after intentional removal.") : s.detail?.site.platform && s.detail.site.platform !== s.publishPlatform && s.publishPlatform ? notice("warn", `This website is registered for ${s.detail.site.platform}; create a separate saved website to publish with another provider.`) : null,
      labelled("Provider", providerSelect, { help: providers.some((p) => p.supported && p.connected) ? "Only connected providers that can host a full site are listed." : "No connected provider can host this site yet. Connect one in Connections first." }),
      misfit ? h("div", { dataset: { incompatible: "true" } }, notice("bad", misfit)) : null,
      !eligible && !misfit ? notice("warn", "Use a connected supported provider, build from current approved records, review every page and record this build as the current local release first.") : null,
      s.confirmingPublish && eligible ? h("div", { role: "group", "aria-label": "Review publication" }, h("p", null, `Approve ${s.detail!.site.siteKind === "independent" ? "separate website " + (s.detail!.site.providerSlug ?? siteId) : s.detail!.site.satelliteId ? "satellite " + s.detail!.site.satelliteId : "client main site"}, build ${b!.recordId}, local release ${local!.releaseNumber}, ${b!.pages.length} pages and ${b!.assets.length} images to ${s.publishPlatform} at ${s.detail!.site.root}. Hash ${b!.releaseHash}.`), confirm,
        h("button", { type: "button", class: "secondary", on: { click: () => { s.confirmingPublish = false; paintSites(); } } }, "Back")) : h("button", { type: "button", id: `sk-publish-${siteId}`, disabled: !eligible, on: { click: () => { s.confirmingPublish = true; paintSites(); } } }, "Review and publish…"),
      s.publishNote ? notice("ok", s.publishNote) : null, s.publicationError ? notice("warn", s.publicationError) : null,
      h("h4", null, "Publication history"), busyButton({ label: "Refresh publication status", busyLabel: "Refreshing…", variant: "secondary", onClick: async () => { await loadSite(siteId); paintSites(); } }),
      h("a", { class: "btn secondary", href: `#/clients/${enc(clientId)}/links` }, "Created links report"),
      ...(s.publications ?? []).map(p => h("div", { class: "sk-publication", dataset: { publicationJob: p.jobId } },
        h("strong", null, `${p.platform}: ${p.state.replaceAll("_", " ")}`), h("p", null, `Job ${p.jobId} · build ${p.buildRecordId} · hash ${sha8(p.releaseHash)}`),
        p.url ? h("p", null, "Provider returned: ", webLink(p.url), " · not yet inspected") : null, p.error ? notice("warn", p.error) : null,
        p.canCancel ? busyButton({ label: "Cancel queued publication", busyLabel: "Cancelling…", variant: "secondary", onClick: async () => {
          try { await api(`/v1/jobs/${enc(p.jobId)}/cancel`, { method: "POST", body: JSON.stringify({ expectedStateVersion: p.stateVersion, confirmCancel: true }) }); await loadPublications(siteId); paintSites(); }
          catch(e) { s.publicationError = describeMediaProblem(e, "Cancellation was not confirmed").message; paintSites(); }
        } }) : null,
        p.reconciliation.length ? h("details", { open: true }, h("summary", null, "Inspect this uncertain outcome before any further publication"), h("ol", null, ...p.reconciliation.map(step => h("li", null, step))), h("details", null, h("summary", null, "Expected files and SHA-256"), ...p.expectedFiles.map(f => h("p", null, `${f.path}: ${f.sha256}`)))) : null)),
      s.publications?.length === 0 ? h("p", { class: "muted" }, "No external publication recorded for this site.") : null);
  }

  function paintSites() {
    if (sitesError) return void siteHost.replaceChildren(errorBanner(sitesError, { retry: () => void loadSites().then(paintSites) }));
    if (sites === null) return void siteHost.replaceChildren(loadingBlock("Loading sites", 2));
    if (selectedSiteId) {
      const createSection = shell.querySelector<HTMLElement>("#sk-create-card");
      if (createSection) createSection.hidden = true;
      if (selectedSiteId.startsWith("legacy:")) {
        if (!selectedLegacy) return void siteHost.replaceChildren(notice("warn", "Website not found. It may have been removed from this client, or the address may belong to another client."));
        const legacy = selectedLegacy;
        const rawUrl = typeof legacy.destinationUrl === "string" ? legacy.destinationUrl : "";
        const safeUrl = (() => { try { const u = new URL(rawUrl); return u.protocol === "https:" || u.protocol === "http:" ? u.href : null; } catch { return null; } })();
        const state = typeof legacy.state === "string" ? legacy.state.replaceAll("_", " ") : "unknown";
        return void siteHost.replaceChildren(
          h("div", { class: "sk-selected-heading" }, h("a", { href: `#/clients/${enc(clientId)}/websites` }, "← Back to websites"), h("p", { class: "muted" }, "Legacy website · read-only provider record")),
          h("section", { class: "card sk-selected-legacy", "aria-labelledby": "sk-legacy-title" },
            h("h2", { id: "sk-legacy-title" }, typeof legacy.name === "string" ? tidy(legacy.name, 120) : safeUrl ? new URL(safeUrl).hostname : "Legacy website"),
            h("div", { class: "sk-legacy-facts" }, h("span", null, `Provider: ${typeof legacy.platform === "string" ? tidy(legacy.platform, 80) : "Not recorded"}`), h("span", null, "Website type: Unknown"), h("span", null, `Status: ${tidy(state, 60)}`), h("span", null, `Updated: ${typeof legacy.lastUpdatedAt === "string" ? tidy(legacy.lastUpdatedAt, 40) : "Time unavailable"}`)),
            safeUrl ? h("p", null, "Saved provider address: ", h("a", { href: safeUrl, target: "_blank", rel: "noopener noreferrer" }, rawUrl, " ↗")) : h("p", null, "No safe provider URL is available for this record."),
            notice("info", "This legacy record does not have an editable CloudPush website source. Its existing content and destination are left untouched."),
            h("a", { class: "btn secondary", href: `#/clients/${enc(clientId)}/site` }, "Browse saved website workspaces"),
          ),
        );
      }
      if (!sites.some((site) => site.siteId === selectedSiteId)) return void siteHost.replaceChildren(notice("warn", "Website not found. It may have been removed from this client, or the address may belong to another client."));
      return void siteHost.replaceChildren(h("div", { class: "sk-selected-heading" }, h("a", { href: `#/clients/${enc(clientId)}/websites` }, "← Back to websites"), h("p", { class: "muted" }, "Selected website workspace")), siteCard(selectedSiteId));
    }
    if (sites.length === 0) return void siteHost.replaceChildren(h("p", { class: "muted", id: "sk-no-sites" }, "No site yet for this client. Choose a kit above and create one."));
    keepScroll(() => siteHost.replaceChildren(
      h("div", { class: "sk-library-heading" }, h("div", null, h("h2", null, "Saved website workspaces"), h("p", { class: "muted" }, "Open one website to edit it, review its history or check publication outcomes.")), h("a", { class: "btn secondary", href: `#/clients/${enc(clientId)}/websites` }, "Website library")),
      ...sites!.map(siteSummary),
    ));
  }
  function siteSummary(site: SiteView): HTMLElement {
    const kit = kits?.find((k) => k.kitId === site.kitId);
    const name = site.root ? (() => { try { return new URL(site.root).hostname; } catch { return site.root; } })() : site.siteId;
    const status = site.removedAt ? "Removed · locked" : site.currentReleaseNumber ? "Release saved locally" : site.latestRevision ? "Draft saved" : "Saved site";
    return h("article", { class: "card sk-site-summary" }, h("div", null, h("strong", null, tidy(name, 160)), h("p", { class: "muted" }, `${kit?.title ?? "Website template unavailable"} · ${site.platform ?? "Provider not recorded"} · ${status}`)), h("a", { class: "btn secondary", href: `#/clients/${enc(clientId)}/site?siteId=${enc(site.siteId)}` }, "Open website →"));
  }
  function keepScroll(fn: () => void) {
    const y = window.scrollY;
    fn();
    window.scrollTo?.(0, y);
  }

  shell.append(
    card("sk-create-card", requestedPlatform ? `Create a website on ${requestedPlatform}` : "Choose a template", h("p", { class: "muted" }, "Complete-website kits. They are composed from the client's approved facts, content and images (plus Google Business Profile links marked owner-confirmed), and from entries you supply for kits that have no stored source. See each one in ", h("a", { href: "#/templates" }, "Template Studio"), "."), createForm),
    flash,
    siteHost,
  );
  paintKits();
  paintSites();
  await Promise.all([loadKits(), loadAux(), loadProviders()]);
  paintKits();
  await loadSites();
  paintSites();
}
